import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Lock, Gift as GiftIcon } from "lucide-react";
import { MinimalShell } from "../components/layout/MinimalShell";
import { LeadForm } from "../components/shared/LeadCapture";
import { GIFT } from "../data/siteContent";
import { TRACKS } from "../data/tracks";
import { useAudio } from "../context/AudioContext";

export default function GiftPage() {
  const [unlocked, setUnlocked] = useState(false);
  const { playTrack, currentId, isPlaying } = useAudio();
  const tracks = GIFT.trackIds.map((id) => TRACKS.find((t) => t.id === id)).filter(Boolean);

  return (
    <MinimalShell>
      <section data-testid="gift-page" className="relative pt-32 md:pt-40 pb-20 overflow-hidden grain min-h-screen">
        <div className="aurora-weave" />
        <div className="line-weave" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[55rem] h-[55rem] radial-glow blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass mb-6">
              <GiftIcon size={15} className="text-[#E7CC86]" />
              <span className="text-[13px] tracking-[0.22em] uppercase text-[#E7CC86]">{GIFT.eyebrow}</span>
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-cream mb-5">
              Three pieces of music, <span className="text-gold">for the moments that matter.</span>
            </h1>
            <p className="text-lg text-cream/70">{GIFT.sub}</p>
          </div>

          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
            {/* Capture */}
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="glass rounded-3xl p-8 md:p-9">
              {!unlocked ? (
                <>
                  <h2 className="font-display text-2xl text-cream mb-2">Where should we send them?</h2>
                  <p className="text-cream/60 mb-6">Unlock all three instantly — and we'll email you the links to keep.</p>
                  <LeadForm source={GIFT.source} avatar="community" cta="Unlock the Gift" onSuccess={() => setUnlocked(true)} />
                </>
              ) : (
                <div data-testid="gift-unlocked" className="text-center py-4">
                  <div className="w-14 h-14 rounded-full btn-gold flex items-center justify-center mx-auto mb-4"><GiftIcon size={26} className="text-[#14100A]" /></div>
                  <h2 className="font-display text-2xl text-cream mb-2">Unlocked. Enjoy.</h2>
                  <p className="text-cream/60">Press play on any piece — the music will follow you as you explore. The links are on their way to your inbox.</p>
                </div>
              )}
            </motion.div>

            {/* Tracks */}
            <div className="space-y-3">
              {tracks.map((t, i) => {
                const active = currentId === t.id;
                const playingThis = active && isPlaying;
                return (
                  <motion.div key={t.id} data-testid={`gift-track-${t.id}`}
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                    className={`glass rounded-2xl p-4 flex items-center gap-4 ${unlocked ? "" : "opacity-80"}`}>
                    <div className="relative w-16 h-16 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-[#12203a] to-[#05070C]">
                      <img loading="lazy" src={t.cover} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-lg text-cream truncate">{t.title}</p>
                      <p className="text-sm text-cream/50 truncate">{t.feat}</p>
                    </div>
                    <button
                      data-testid={`gift-play-${t.id}`}
                      aria-label={`Play ${t.title}`}
                      disabled={!unlocked}
                      onClick={() => unlocked && playTrack(t.id)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${unlocked ? "btn-gold" : "border border-[#C9A24B]/30 text-cream/40"}`}
                    >
                      {!unlocked ? <Lock size={18} /> : playingThis ? <Pause size={18} className="text-[#14100A]" fill="#14100A" /> : <Play size={18} className="ml-0.5 text-[#14100A]" fill="#14100A" />}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </MinimalShell>
  );
}
